/* Created by Interactive 63 - https://interactive63.com */
/**
 * The project session: ties the store to storage. It reopens the last project at start-up,
 * autosaves changes, opens and saves project files, returns to the start screen, manages the
 * recent-projects list, and warns when another tab changes the open project.
 */
import { PROJECT_FILE_EXTENSION } from '../../core/branding';
import { createEmptyProject } from '../../core/model/defaults';
import { createId } from '../../core/model/ids';
import type { Project } from '../../core/model/types';
import { setExpanded } from '../store/actions';
import type { Store } from '../store/store';
import { h } from '../ui/dom';
import { showMessageDialog } from '../ui/message-dialog';
import { promptDialog } from '../ui/prompt-dialog';
import { toast } from '../ui/toast';
import { Autosaver } from './autosave';
import {
  downloadProjectFile,
  parseProjectFile,
  saveProjectFile,
  withOpenedId,
} from './project-file';
import { ProjectStorage, type IndexEntry } from './project-storage';
import type { Sanitiser } from './project-record';
import { TabSync } from './tab-sync';
import { buildThumbnail } from './thumbnail';

export interface SessionOptions {
  storage?: ProjectStorage;
  /** Overrides the SVG sanitiser, for tests that run without a real browser DOM. */
  sanitise?: Sanitiser;
}

const OPEN_ERROR_HEADING = "This project file couldn't be opened";
const MAX_SHOWN_ERRORS = 5;

export class ProjectSession {
  readonly storage: ProjectStorage;
  readonly autosaver: Autosaver;
  private readonly sync = new TabSync();
  private banner: HTMLElement | undefined;

  /** Creates a session for the store. Call `start` once the editor is built. */
  constructor(
    private readonly store: Store,
    private readonly options: SessionOptions = {},
  ) {
    this.storage = options.storage ?? new ProjectStorage('svg-tempo', options.sanitise);
    this.autosaver = new Autosaver(store, this.storage, {
      thumbnail: buildThumbnail,
      onSaved: (id) => {
        this.sync.notifySaved(id);
      },
      onWarning: (message) => {
        toast.warning(message);
      },
    });
  }

  /** Starts autosaving and listening for changes made in other tabs. */
  start(): void {
    this.autosaver.start();
    this.sync.onSavedElsewhere((projectId) => {
      if (projectId === this.store.getState().project.id) this.showChangedBanner();
    });
  }

  /** Reopens the project that was open last time, if there is one. */
  async restore(): Promise<void> {
    try {
      const id = await this.storage.lastOpenId();
      if (id === undefined) return;
      const result = await this.storage.load(id);
      if (result?.ok !== true) return;
      this.store.replaceProject(result.project);
      this.autosaver.markClean();
    } catch (error) {
      console.warn('Could not reopen the last project', error);
    }
  }

  /** Makes a project the open one. The previous project is saved first and stays in Recent. */
  install(project: Project): void {
    void this.autosaver.flush();
    this.store.replaceProject(project);
    setExpanded(
      this.store,
      project.rootIds.filter((id) => (project.elements[id]?.childIds.length ?? 0) > 0),
      true,
    );
  }

  /** Saves the open project and returns to the start screen with a blank project. */
  async goHome(): Promise<void> {
    if (Object.keys(this.store.getState().project.elements).length === 0) return;
    await this.autosaver.flush();
    this.store.replaceProject(createEmptyProject('Untitled'));
    this.autosaver.markClean();
    await this.storage.setLastOpenId(undefined).catch(() => undefined);
  }

  /** Lists recent projects, or an empty list if storage is unavailable. */
  async recent(): Promise<IndexEntry[]> {
    try {
      return await this.storage.list();
    } catch {
      return [];
    }
  }

  /** Opens a stored project by ID. */
  async openStored(id: string): Promise<void> {
    try {
      const result = await this.storage.load(id);
      if (result?.ok !== true) {
        toast.error("That project couldn't be opened.");
        return;
      }
      await this.autosaver.flush();
      this.store.replaceProject(result.project);
      this.autosaver.markClean();
      await this.storage.setLastOpenId(id);
    } catch (error) {
      console.warn('Could not open project', error);
      toast.error("That project couldn't be opened.");
    }
  }

  /** Renames a stored project. */
  async rename(id: string, name: string): Promise<void> {
    const trimmed = name.trim();
    const result = await this.storage.load(id);
    if (trimmed === '' || result?.ok !== true) return;
    const project = { ...result.project, name: trimmed, updatedAt: new Date().toISOString() };
    await this.storage.save(project, buildThumbnail(project));
  }

  /** Saves a copy of a stored project under a new ID. */
  async duplicate(id: string): Promise<void> {
    const result = await this.storage.load(id);
    if (result?.ok !== true) return;
    const now = new Date().toISOString();
    const copy = {
      ...result.project,
      id: createId('proj'),
      name: `${result.project.name} copy`,
      createdAt: now,
      updatedAt: now,
    };
    await this.storage.save(copy, buildThumbnail(copy));
  }

  /** Downloads a stored project as a project file. */
  async download(id: string): Promise<void> {
    const result = await this.storage.load(id);
    if (result?.ok === true) downloadProjectFile(result.project);
  }

  /** Deletes a stored project. */
  async remove(id: string): Promise<void> {
    await this.storage.remove(id);
  }

  /** Opens a project file chosen through the file picker. */
  chooseProjectFile(): void {
    const input = h('input', {
      type: 'file',
      accept: PROJECT_FILE_EXTENSION,
    });
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) void this.openProjectFile(file);
    });
    input.click();
  }

  /** Reads, validates and opens a project file. */
  async openProjectFile(file: File): Promise<void> {
    let text: string;
    try {
      text = await file.text();
    } catch {
      showMessageDialog(OPEN_ERROR_HEADING, ["The file couldn't be read."]);
      return;
    }
    const result = parseProjectFile(text, this.options.sanitise);
    if (!result.ok) {
      showMessageDialog(OPEN_ERROR_HEADING, result.errors.slice(0, MAX_SHOWN_ERRORS));
      return;
    }
    const exists = await this.storage.has(result.project.id).catch(() => false);
    this.install(withOpenedId(result.project, exists));
  }

  /** Saves the open project as a project file (Ctrl + S). */
  async saveProjectFile(): Promise<void> {
    const { project } = this.store.getState();
    if (Object.keys(project.elements).length === 0) return;
    if (await saveProjectFile(project)) toast.info('Project saved.');
  }

  /** Asks for a name, then saves the open project as a new project and file. */
  async promptSaveAs(): Promise<void> {
    const name = await promptDialog({
      title: 'Save as',
      label: 'Project name',
      value: `${this.store.getState().project.name} copy`,
      confirmLabel: 'Save',
    });
    if (name !== null) await this.saveProjectAs(name);
  }

  /**
   * Saves the open project under a new name as a separate project, then writes it to a file. The
   * original stays in Recent as it was; further edits go to the copy.
   */
  async saveProjectAs(name: string): Promise<void> {
    const { project } = this.store.getState();
    if (Object.keys(project.elements).length === 0) return;
    await this.autosaver.flush();
    const now = new Date().toISOString();
    const trimmed = name.trim();
    this.store.updateProjectSilently((draft) => {
      draft.id = createId('proj');
      draft.name = trimmed === '' ? project.name : trimmed;
      draft.createdAt = now;
    });
    await this.autosaver.saveNow();
    const copy = this.store.getState().project;
    if (await saveProjectFile(copy, { forcePicker: true })) toast.info('Project saved.');
  }

  /** Shows the banner offering to reload a project changed in another tab. */
  private showChangedBanner(): void {
    if (this.banner?.isConnected) return;
    const dismiss = (): void => {
      this.banner?.remove();
      this.banner = undefined;
    };
    const reload = h('button', { type: 'button', class: 'primary' }, 'Reload');
    const keep = h('button', { type: 'button' }, 'Keep mine');
    reload.addEventListener('click', () => {
      dismiss();
      void this.reloadOpenProject();
    });
    keep.addEventListener('click', () => {
      dismiss();
      void this.autosaver.saveNow();
    });
    this.banner = h(
      'div',
      { class: 'banner', role: 'alert' },
      h('span', null, 'This project was changed in another tab. Reload it?'),
      reload,
      keep,
    );
    document.body.append(this.banner);
  }

  /** Replaces the open project with the stored version. */
  private async reloadOpenProject(): Promise<void> {
    const result = await this.storage.load(this.store.getState().project.id);
    if (result?.ok !== true) return;
    this.store.replaceProject(result.project);
    this.autosaver.markClean();
  }
}
